import * as joint from '@clientio/rappid';
import { TranslocoService } from '@jsverse/transloco';
import { SelectionType } from 'guideline/domain';
import { ComponentConverter } from 'shared/domain';

import {
  ActivityDefinitionDto,
  ComponentDto,
  ComponentType,
  FlowchartDecisionNodeDto,
  FlowchartElementType,
  FlowchartLinkDto,
  ProposedComponentDto,
} from '@treetop/shared/model';

import { BasicNode } from './BasicNode';
import { IFlowchartNode } from './IFlowchartNode';
import { FlowchartSidebarStoreService } from '../../../../../guideline/feature-sidebar/src/lib/infrastructure/flowchart-sidebar-store.service';
import OperationsService from '../infrastructure/operations/operations.service';
import SubflowService from '../infrastructure/operations/subflow.service';

export class FlowchartDecisionNode extends BasicNode<FlowchartDecisionNodeDto> implements IFlowchartNode {
  constructor(element: FlowchartDecisionNodeDto) {
    super(element);
  }

  addOutgoingLink(link: FlowchartLinkDto): void {
    this.element.outgoingLinks = this.element.outgoingLinks || [];
    this.element.outgoingLinks.push(link);
  }

  removeOutgoingLink(linkIndex: number): void {
    this.element.outgoingLinks.splice(linkIndex, 1);
  }

  addToGraph(
    graph: joint.dia.Graph,
    paper: joint.dia.Paper,
    operationsService: OperationsService,
    subflowService: SubflowService,
    translocoService?: TranslocoService,
    allowEvents = false,
  ): void {
    const title = this.element.guidelineCriteria ? this.element.guidelineCriteria.title : this.element.guidelineObservation?.title;
    const size = {
      width: this.element.width,
      height: this.element.height,
    };
    const prettifiedRule = this.element.rulePrettyfied
      ? new DOMParser().parseFromString(this.element.rulePrettyfied || this.element.rule, 'text/html').documentElement.textContent
      : this.element.rule;
    const titleDecisionNode = title || this.element.ruleTitle || prettifiedRule || 'no title';
    const decisionElement = operationsService.decisionNodeService.createDecision(titleDecisionNode, {
      x: this.element.positionX,
      y: this.element.positionY,
      tooltip: titleDecisionNode,
      id: this.element.id,
      uniqueId: this.element.uniqueId,
      size,
      colorPresetId: this.element.colorPresetId,
    });
    graph.addCell(decisionElement);

    const description =
      this.element?.guidelineCriteria?.description || this.element?.guidelineObservation?.description || this.element?.ruleDescription;

    operationsService.setClickableForSmartbook(this.element.id, description);
    operationsService.addElementTags(this.element);

    if (this.element?.showDescription) {
      operationsService.initElementsDescription(this.element.id, description);
    }
  }

  setPropertiesForInit(element: joint.dia.Cell): void {
    super.setPropertiesForInit(element);
    this.element.elementType = FlowchartElementType.FlowchartDecisionNode;
  }

  async setPropertiesAfterSearch(
    definition: ComponentDto | ProposedComponentDto,
    medicationRequests: ActivityDefinitionDto[],
    componentConverter: ComponentConverter,
    selectedElementView: joint.dia.ElementView,
    translocoService?: TranslocoService,
    flowchartSidebarStoreService?: FlowchartSidebarStoreService,
  ): Promise<void> {
    const copiedElement: FlowchartDecisionNodeDto = JSON.parse(JSON.stringify(this.element));

    if (definition.code.startsWith('PC-')) {
      copiedElement.guidelineCriteria = componentConverter.convertProposedComponentToCriterion(definition);
      copiedElement.guidelineCriteriaId = copiedElement.guidelineCriteria.id;
    } else if ((definition as ComponentDto).type === ComponentType.FindingDefinition) {
      copiedElement.guidelineCriteria = componentConverter.convertFindingToCriterion(definition);
      copiedElement.guidelineCriteriaId = copiedElement.guidelineCriteria.id;
      this._resetGuidelineObservation();
    } else if ((definition as ComponentDto).type === ComponentType.ObservationDefinition) {
      copiedElement.guidelineObservation = componentConverter.convertObservationToGuidelineObservation(definition);
      copiedElement.guidelineObservationId = copiedElement.guidelineObservation.id;
      this._resetGuidelineCriteria();
    }

    if (selectedElementView) {
      copiedElement.width = selectedElementView.model.attributes.size.width;
      copiedElement.height = selectedElementView.model.attributes.size.height;
    }

    this.element = copiedElement;

    flowchartSidebarStoreService.setDto({
      element: copiedElement,
      type: SelectionType.Flowchart,
    });
  }

  _resetGuidelineCriteria() {
    this.element.guidelineCriteria = null;
    this.element.guidelineCriteriaId = null;
  }

  _resetGuidelineObservation() {
    this.element.guidelineObservation = null;
    this.element.guidelineObservationId = null;
  }
}
